if (!customElements.get('babauba-size-advisor')) {
  class BabaubaSizeAdvisor extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.profileSelect = this.querySelector('[data-size-advisor-profile]');
      this.form = this.querySelector('[data-size-advisor-form]');
      this.heightInput = this.querySelector('[data-size-advisor-height]');
      this.result = this.querySelector('[data-size-advisor-result]');
      this.error = this.querySelector('[data-size-advisor-error]');
      this.measurement = this.querySelector('[data-size-advisor-measurement]');
      this.selectButton = this.querySelector('[data-size-advisor-select]');
      this.messages = this.readMessages();
      this.availability = this.readAvailability();
      this.recommendedSize = null;

      this.sortTableEntries();

      this.profileSelect?.addEventListener('change', () => this.activateProfile(this.profileSelect.value));
      this.form?.addEventListener('submit', (event) => this.calculate(event));
      this.selectButton?.addEventListener('click', () => this.selectRecommendedSize());

      const initialPanel = this.getInitialPanel();
      if (initialPanel) {
        const initialProfileId = initialPanel.dataset.sizeAdvisorPanel;
        if (this.profileSelect) this.profileSelect.value = initialProfileId;
        this.activateProfile(initialProfileId);
      }
    }

    normalizeSize(value) {
      return String(value || '')
        .normalize('NFKC')
        .toLowerCase()
        .trim()
        .replace(/[\s\u00a0]/g, '')
        .replace(/[–—-]/g, '/');
    }

    sizeToken(value) {
      return this.normalizeSize(value).match(/\d{2,3}(?:\/\d{2,3})?/)?.[0] || '';
    }

    sizesMatch(candidate, expected) {
      const normalizedCandidate = this.normalizeSize(candidate);
      const normalizedExpected = this.normalizeSize(expected);
      if (!normalizedCandidate || !normalizedExpected) return false;
      if (normalizedCandidate === normalizedExpected) return true;

      const candidateToken = this.sizeToken(candidate);
      const expectedToken = this.sizeToken(expected);
      return Boolean(candidateToken && expectedToken && candidateToken === expectedToken);
    }

    readMessages() {
      const template = this.querySelector('[data-size-advisor-messages]');
      if (!template) return {};
      return Array.from(template.content.querySelectorAll('[data-message]')).reduce((messages, node) => {
        messages[node.dataset.message] = node.textContent.trim();
        return messages;
      }, {});
    }

    readAvailability() {
      const script = this.querySelector('script[data-size-advisor-availability]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (_error) {
        return [];
      }
    }

    sortTableEntries() {
      this.querySelectorAll('[data-size-advisor-panel] tbody').forEach((tbody) => {
        Array.from(tbody.querySelectorAll('[data-size-entry]'))
          .sort((a, b) => Number(a.dataset.sortOrder) - Number(b.dataset.sortOrder))
          .forEach((row) => tbody.appendChild(row));
      });
    }

    get activePanel() {
      return this.querySelector('[data-size-advisor-panel]:not([hidden])');
    }

    getInitialPanel() {
      const panels = Array.from(this.querySelectorAll('[data-size-advisor-panel]'));
      if (this.dataset.preferSingleSizes !== 'true') return panels[0];

      return (
        panels.find((panel) => {
          const sizeLabels = Array.from(panel.querySelectorAll('[data-size-entry]')).map((row) =>
            this.normalizeSize(row.dataset.size)
          );
          return sizeLabels.length > 0 && sizeLabels.every((size) => !size.includes('/'));
        }) || panels[0]
      );
    }

    activateProfile(profileId) {
      this.querySelectorAll('[data-size-advisor-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.sizeAdvisorPanel !== profileId;
      });
      const panel = this.activePanel;
      if (panel && this.measurement) this.measurement.textContent = panel.dataset.measurementNote || '';
      this.resetFeedback();
    }

    calculate(event) {
      event.preventDefault();
      this.resetFeedback();

      if (!this.form.reportValidity()) return;
      const height = Number(this.heightInput.value);
      if (!Number.isFinite(height)) {
        this.showError(this.messages.invalid);
        return;
      }

      const entries = Array.from(this.activePanel?.querySelectorAll('[data-size-entry]') || []).sort(
        (a, b) => Number(a.dataset.sortOrder) - Number(b.dataset.sortOrder)
      );
      const entry = entries.find(
        (row) => height >= Number(row.dataset.recommendMin) && height <= Number(row.dataset.recommendMax)
      );

      if (!entry) {
        this.showError(this.messages['no-match']);
        return;
      }

      this.recommendedSize = entry.dataset.size;
      this.querySelector('[data-size-advisor-result-size]').textContent = this.recommendedSize;
      this.querySelector('[data-size-advisor-result-range]').textContent = this.dataset.rangeTemplate
        .replace('__MIN__', entry.dataset.recommendMin)
        .replace('__MAX__', entry.dataset.recommendMax);
      this.querySelector('[data-size-advisor-result-note]').textContent = this.activePanel?.dataset.profileNote || '';
      this.result.hidden = false;
      this.updateProductAvailability();
      this.result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
    }

    updateProductAvailability() {
      if (this.dataset.productAdvisor !== 'true') return;
      const availabilityNode = this.querySelector('[data-size-advisor-availability]:not(script)');
      const matchingVariants = this.availability.filter((item) => this.sizesMatch(item.size, this.recommendedSize));
      const control = this.findSizeControl(this.recommendedSize);
      const controlUnavailable = control?.matches('input.disabled, input:disabled, option:disabled');
      const isAvailable = matchingVariants.some((item) => item.available) && !controlUnavailable;

      this.selectButton.hidden = !isAvailable || !control;
      availabilityNode.dataset.state = isAvailable ? 'available' : 'unavailable';
      if (!matchingVariants.length || !control) {
        availabilityNode.textContent = this.messages['not-offered'];
      } else {
        availabilityNode.textContent = isAvailable ? this.messages.available : this.messages.unavailable;
      }
    }

    findSizeControl(size) {
      const productInfo = this.closest('product-info');
      const variantPicker =
        document.getElementById(`variant-selects-${this.dataset.productSection}`) ||
        productInfo?.querySelector('variant-selects') ||
        document.querySelector(`variant-selects[data-section="${this.dataset.productSection}"]`);
      if (!variantPicker) return null;

      const position = Number(this.dataset.sizeOptionPosition);
      const optionFields = Array.from(variantPicker.querySelectorAll('.product-form__input'));
      const sizeField = Number.isInteger(position) && position > 0 ? optionFields[position - 1] : null;
      const searchRoot = sizeField || variantPicker;

      const radio = Array.from(searchRoot.querySelectorAll('input[type="radio"]')).find((input) =>
        this.sizesMatch(input.value, size)
      );
      if (radio) return radio;

      const selects = Array.from(searchRoot.querySelectorAll('select'));
      const select =
        selects.find((element) => element.name === `options[${this.dataset.sizeOptionName}]`) || selects[0];
      return Array.from(select?.options || []).find((option) => this.sizesMatch(option.value, size)) || null;
    }

    selectRecommendedSize() {
      const control = this.findSizeControl(this.recommendedSize);
      if (!control) return;

      control.closest('variant-selects')?.selectOption(control, 'advisor');

      const message = this.dataset.selectedMessage.replace('__SIZE__', this.recommendedSize);
      this.querySelector('[data-size-advisor-availability]').textContent = message;
      const modal = this.closest('basic-modal');
      if (modal?.hide) modal.hide();
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.heightInput.setAttribute('aria-invalid', 'true');
      this.heightInput.focus();
    }

    resetFeedback() {
      this.recommendedSize = null;
      if (this.result) this.result.hidden = true;
      if (this.error) this.error.hidden = true;
      if (this.heightInput) this.heightInput.removeAttribute('aria-invalid');
      if (this.selectButton) this.selectButton.hidden = true;
    }
  }

  customElements.define('babauba-size-advisor', BabaubaSizeAdvisor);
}
